import React, { useState, useEffect } from 'react';
import { getRestaurantInfo } from '../services/api';
import { MapPin, Mail, Clock, ExternalLink, Instagram, Facebook, Settings } from 'lucide-react';

const Footer = ({ onAdminClick }) => {
  const currentYear = new Date().getFullYear();
  const [restaurantInfo, setRestaurantInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const info = await getRestaurantInfo();
        setRestaurantInfo(info);
      } catch (error) {
        console.error('Error fetching restaurant info:', error);
        const savedInfo = localStorage.getItem('restaurantInfo');
        if (savedInfo) setRestaurantInfo(JSON.parse(savedInfo));
      }
    };
    fetchData();
  }, []);

  if (!restaurantInfo) return null;

  return (
    <footer className="bg-black text-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div>
            <h3
              className="text-3xl font-bold mb-4"
              style={{ fontFamily: "'Crimson Text', serif" }}>

              {restaurantInfo.name}
            </h3>
            <p className="text-gray-400 leading-relaxed mb-4">
              {restaurantInfo.tagline}
            </p>
            {/* Social Media Icons */}
            {restaurantInfo.social && (
              <div className="flex space-x-4">
                {restaurantInfo.social.instagram && (
                  <a 
                    href={restaurantInfo.social.instagram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-pink-400 transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                )}
                {restaurantInfo.social.facebook && (
                  <a 
                    href={restaurantInfo.social.facebook} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-pink-400 transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="!font-semibold !text-xl mb-4">Contacto</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 flex-shrink-0" />
                <span className="text-gray-400">{restaurantInfo.email}</span>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-1" />
                <div>
                  <span className="text-gray-400">{restaurantInfo.address}</span>
                  {restaurantInfo.mapsUrl && (
                    <a 
                      href={restaurantInfo.mapsUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-pink-400 hover:text-pink-300 mt-1"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver en Google Maps
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4 className="!font-semibold !text-xl mb-4">Horarios</h4>
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 flex-shrink-0 mt-1" />
                <div className="text-gray-400">
                  <p>{restaurantInfo.hours.weekday}</p>
                  {restaurantInfo.hours.weekend && <p className="mt-1">{restaurantInfo.hours.weekend}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-center gap-4 relative">
          <p className="text-gray-400 text-center">
            © {currentYear} {restaurantInfo.name}. All rights reserved.
          </p>
          {onAdminClick && (
            <button
              onClick={onAdminClick}
              className="sm:absolute sm:right-0 text-gray-500 hover:text-pink-400 transition-colors p-2 rounded-full hover:bg-gray-900"
              title="Abrir Panel de Administración"
              aria-label="Abrir Panel de Administración"
              data-testid="open-admin-panel-btn"
            >
              <Settings className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </footer>);

};

export default Footer;